/** Admin — Configurazione Comune (BC-3). Self-service settings by section. */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Loader2, Save, Building2, Phone, Share2, SlidersHorizontal, Scale } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { CoverImage, CoverUpload } from '@/components/media/Cover';
import { getConfig, updateConfig } from '@/services/api/config';
import { formatApiError } from '@/services/api/client';
import { SETTINGS } from '@/constants/testIds';

const Section = ({ icon: Icon, title, children }) => (
	<section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5 sm:p-6">
		<div className="mb-4 flex items-center gap-2 text-emerald-300">
			<Icon size={16} />
			<h2 className="text-sm font-semibold uppercase tracking-widest">{title}</h2>
		</div>
		<div className="grid gap-4 sm:grid-cols-2">{children}</div>
	</section>
);

const Field = ({ label, testId, value, onChange, full, placeholder, type = 'text' }) => (
	<div className={full ? 'sm:col-span-2' : ''}>
		<Label className="text-xs text-slate-400">{label}</Label>
		<Input data-testid={testId} type={type} value={value || ''} placeholder={placeholder}
			onChange={(e) => onChange(e.target.value)}
			className="mt-1 border-slate-700 bg-slate-950/50 text-slate-100" />
	</div>
);

const Toggle = ({ label, hint, testId, checked, onChange }) => (
	<div className="flex items-center justify-between gap-4 rounded-lg border border-slate-800 bg-slate-950/40 p-3 sm:col-span-2">
		<div><p className="text-sm text-slate-200">{label}</p>{hint && <p className="text-xs text-slate-500">{hint}</p>}</div>
		<Switch data-testid={testId} checked={!!checked} onCheckedChange={onChange} />
	</div>
);

export default function SettingsPage() {
	const [searchParams] = useSearchParams();
	const municipalityId = searchParams.get('municipality_id') || undefined;
	const { data, isLoading, isError } = useQuery({ queryKey: ['config', municipalityId], queryFn: () => getConfig(municipalityId), retry: false });
	const [form, setForm] = useState(null);

	useEffect(() => {
		if (data && !form) {
			setForm({
				name: data.name || '',
				short_description: data.short_description || '',
				territory_description: data.territory_description || '',
				logo_media_id: data.logo_media_id || null,
				hero_media_id: data.hero_media_id || null,
				logo_url: data.logo_url || null,
				hero_url: data.hero_url || null,
				contacts: { ...data.contacts },
				social: { ...data.social, other: data.social?.other || [] },
				operational: { ...data.operational },
				legal: { ...data.legal },
				language: data.language || 'it',
			});
		}
	}, [data, form]);

	const save = useMutation({
		mutationFn: (body) => updateConfig(body, municipalityId),
		onSuccess: () => toast.success('Configurazione salvata'),
		onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
	});

	if (isError) {
		return (
			<div data-testid="settings-select-municipality-hint" className="mx-auto max-w-4xl rounded-2xl border border-slate-800 bg-slate-900/40 p-8 text-slate-400">
				Seleziona un Comune dalla sezione <span className="text-emerald-300">Comuni</span> per gestirne la configurazione.
			</div>
		);
	}

	if (isLoading || !form) {
		return (
			<div data-testid={SETTINGS.loading} className="flex items-center gap-2 text-slate-400">
				<Loader2 className="animate-spin" size={16} /> Caricamento configurazione…
			</div>
		);
	}

	const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
	const setGroup = (g, k, v) => setForm((f) => ({ ...f, [g]: { ...f[g], [k]: v } }));

	const onSave = () => {
		const { logo_url, hero_url, ...body } = form;
		save.mutate(body);
	};

	return (
		<div data-testid={SETTINGS.page} className="mx-auto max-w-4xl space-y-6">
			<div className="flex items-center justify-between">
				<h1 className="text-3xl font-extrabold" style={{ fontFamily: "'Poppins', sans-serif" }}>Configurazione</h1>
				<Button data-testid={SETTINGS.saveButton} onClick={onSave} disabled={save.isPending}
					className="bg-emerald-400 text-slate-950 hover:bg-emerald-300">
					{save.isPending ? <Loader2 size={16} className="mr-1 animate-spin" /> : <Save size={16} className="mr-1" />} Salva
				</Button>
			</div>

			<Section icon={Building2} title="Identità">
				<Field label="Nome Comune" testId={SETTINGS.nameInput} value={form.name} onChange={(v) => set('name', v)} />
				<Field label="Descrizione breve" testId={SETTINGS.shortDescInput} value={form.short_description} onChange={(v) => set('short_description', v)} />
				<div className="sm:col-span-2">
					<Label className="text-xs text-slate-400">Descrizione del territorio</Label>
					<Textarea data-testid={SETTINGS.territoryInput} value={form.territory_description}
						onChange={(e) => set('territory_description', e.target.value)}
						className="mt-1 border-slate-700 bg-slate-950/50 text-slate-100" rows={3} />
				</div>
				<div>
					<Label className="text-xs text-slate-400">Logo</Label>
					<div className="mt-1">
						{form.logo_media_id && <CoverImage url={form.logo_url} className="mb-2 h-20 w-20 rounded-lg" icon={Building2} />}
						<CoverUpload previewUrl={form.logo_url} testId="settings-logo-upload"
							onChange={(id) => set('logo_media_id', id)} />
					</div>
				</div>
				<div>
					<Label className="text-xs text-slate-400">Immagine Hero</Label>
					<div className="mt-1">
						<CoverUpload previewUrl={form.hero_url} testId="settings-hero-upload"
							onChange={(id) => set('hero_media_id', id)} />
					</div>
				</div>
			</Section>

			<Section icon={Phone} title="Contatti">
				<Field label="Indirizzo" full testId={SETTINGS.addressInput} value={form.contacts.address} onChange={(v) => setGroup('contacts', 'address', v)} />
				<Field label="Telefono" testId={SETTINGS.phoneInput} value={form.contacts.phone} onChange={(v) => setGroup('contacts', 'phone', v)} />
				<Field label="Email" testId={SETTINGS.emailInput} value={form.contacts.email} onChange={(v) => setGroup('contacts', 'email', v)} placeholder="info@comune.it" />
				<Field label="PEC (opzionale)" testId={SETTINGS.pecInput} value={form.contacts.pec} onChange={(v) => setGroup('contacts', 'pec', v)} />
				<Field label="Sito istituzionale" testId={SETTINGS.websiteInput} value={form.contacts.website} onChange={(v) => setGroup('contacts', 'website', v)} placeholder="https://…" />
			</Section>

			<Section icon={Share2} title="Social">
				<Field label="Facebook" testId={SETTINGS.facebookInput} value={form.social.facebook} onChange={(v) => setGroup('social', 'facebook', v)} placeholder="https://facebook.com/…" />
				<Field label="Instagram" testId={SETTINGS.instagramInput} value={form.social.instagram} onChange={(v) => setGroup('social', 'instagram', v)} placeholder="https://instagram.com/…" />
				<Field label="YouTube" testId={SETTINGS.youtubeInput} value={form.social.youtube} onChange={(v) => setGroup('social', 'youtube', v)} placeholder="https://youtube.com/…" />
			</Section>

			<Section icon={SlidersHorizontal} title="Impostazioni operative">
				<Toggle label="Operatori possono pubblicare" hint="Secondo le policy RBAC esistenti"
					testId={SETTINGS.operatorsPublishSwitch} checked={form.operational.operators_can_publish}
					onChange={(v) => setGroup('operational', 'operators_can_publish', v)} />
				<Toggle label="Gestione attività locali abilitata"
					testId={SETTINGS.localBusinessSwitch} checked={form.operational.local_business_enabled}
					onChange={(v) => setGroup('operational', 'local_business_enabled', v)} />
				<Toggle label="Archiviazione automatica eventi passati" hint="Applicata da un'attività di manutenzione pianificata"
					testId={SETTINGS.autoArchiveSwitch} checked={form.operational.auto_archive_past_events}
					onChange={(v) => setGroup('operational', 'auto_archive_past_events', v)} />
			</Section>

			<Section icon={Scale} title="Privacy e note legali">
				<Field label="URL Privacy Policy" full testId={SETTINGS.privacyInput} value={form.legal.privacy_url} onChange={(v) => setGroup('legal', 'privacy_url', v)} placeholder="https://…" />
				<Field label="URL Cookie Policy" full testId={SETTINGS.cookieInput} value={form.legal.cookie_url} onChange={(v) => setGroup('legal', 'cookie_url', v)} placeholder="https://…" />
				<Field label="URL Note legali" full testId={SETTINGS.legalInput} value={form.legal.legal_notes_url} onChange={(v) => setGroup('legal', 'legal_notes_url', v)} placeholder="https://…" />
			</Section>
		</div>
	);
}
